import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ClientDashboard from "./pages/ClientDashboard";
import ProfilePage from "./pages/ProfilePage";
import TemplateSelection from "./pages/TemplateSelection";
import TemplateForm from "./pages/TemplateForm";
import SubmissionPreview from "./pages/SubmissionPreview";
import VendorPortalPage from "./pages/VendorPortalPage";
import DepartmentPortalPage from "./pages/DepartmentPortalPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSubmissions from "./pages/AdminSubmissions";
import AdminUsers from "./pages/AdminUsers";
import AdminTemplates from "./pages/AdminTemplates";
import AdminSettings from "./pages/AdminSettings";
import AdminDatabase from "./pages/AdminDatabase";
import MFASetupPage from "./pages/MFASetupPage";
import TeamsPage from "./pages/TeamsPage";
import AssessmentsPage, { PortalAssessmentView } from "./pages/AssessmentsPage";
import AuditorPortalPage from "./pages/AuditorPortalPage";

import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#004E98] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Force admins to set up MFA before accessing anything
  if (user.is_admin && !user.mfa_enabled) {
    return <Navigate to="/setup-mfa" replace />;
  }
  
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[#004E98] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={user.is_admin ? "/admin" : "/dashboard"} replace />;
  }
  
  return children;
};

function AssessmentVendorWrapper() {
  const { vendorToken } = useParams();
  return <PortalAssessmentView token={vendorToken} portalType="vendor" />;
}

function AssessmentDeptWrapper() {
  const { deptToken } = useParams();
  return <PortalAssessmentView token={deptToken} portalType="department" />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/setup-mfa" element={<MFASetupPage />} />
      <Route path="/vendor/:vendorToken" element={<VendorPortalPage />} />
      <Route path="/department/:deptToken" element={<DepartmentPortalPage />} />
      <Route path="/auditor/:token" element={<AuditorPortalPage />} />
      
      {/* Client Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplateSelection /></ProtectedRoute>} />
      <Route path="/templates/:templateId/new" element={<ProtectedRoute><TemplateForm /></ProtectedRoute>} />
      <Route path="/submissions/:submissionId" element={<ProtectedRoute><TemplateForm /></ProtectedRoute>} />
      <Route path="/submissions/:submissionId/preview" element={<ProtectedRoute><SubmissionPreview /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
      <Route path="/assessments" element={<ProtectedRoute><AssessmentsPage /></ProtectedRoute>} />
      <Route path="/assessment-vendor/:vendorToken" element={<AssessmentVendorWrapper />} />
      <Route path="/assessment-department/:deptToken" element={<AssessmentDeptWrapper />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/submissions" element={<ProtectedRoute adminOnly><AdminSubmissions /></ProtectedRoute>} />
      <Route path="/admin/submissions/:submissionId" element={<ProtectedRoute adminOnly><SubmissionPreview isAdmin /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/templates" element={<ProtectedRoute adminOnly><AdminTemplates /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/database" element={<ProtectedRoute adminOnly><AdminDatabase /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: { fontFamily: 'Inter, sans-serif' }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
